<template>
  <div v-if="show" class="push-prompt">
    <div class="push-prompt-text">{{ $t('notifications.pushPromptText') }}</div>
    <div class="push-prompt-actions">
      <button class="btn btn-sm btn-dark mb-0" @click="enable">{{ $t('notifications.pushPromptEnable') }}</button>
      <button class="btn btn-sm btn-link mb-0" @click="dismiss">{{ $t('notifications.pushPromptDismiss') }}</button>
    </div>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import api from "../../services/api";
import { isPushSupported, ensurePushPermission, subscribeToPush, isPushConfigured, getPushPermission } from "../../services/pushNotifications";

const DISMISSED_KEY = "pushPromptDismissed";
const SYNCED_AT_KEY = "pushAutoSubscribedAt";
const RESYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

// Keeps this device subscribed to push without bothering the user:
// - permission already granted -> silently (re)subscribe at most once a day
// - permission never asked -> offer a one-time banner; "not now" is remembered
// - permission denied -> do nothing (the browser would not re-prompt anyway)
export default {
  name: "PushPrompt",
  data() {
    return {
      show: false,
      publicKey: null,
    };
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      const token = Cookies.get("accessToken");
      if (!token || !isPushSupported()) return;

      const permission = await getPushPermission();
      if (permission === "denied") return;
      if (permission === "default" && localStorage.getItem(DISMISSED_KEY)) return;
      if (permission === "granted") {
        const syncedAt = Number(localStorage.getItem(SYNCED_AT_KEY) || 0);
        if (Date.now() - syncedAt < RESYNC_INTERVAL_MS) return;
      }

      try {
        const userId = jwtDecode(token).UserIdentifier;
        const response = await api.get(`/Notification/settings/${userId}`);
        if (!isPushConfigured(response.data) || !response.data.pushEnabled) return;
        this.publicKey = response.data.pushPublicKey;

        if (permission === "granted") {
          await subscribeToPush(this.publicKey);
          localStorage.setItem(SYNCED_AT_KEY, String(Date.now()));
        } else {
          this.show = true;
        }
      } catch (error) {
        console.error('Push auto-subscribe failed:', error); // eslint-disable-line no-console
      }
    },

    async enable() {
      try {
        // Prompt first, directly in the click gesture (iOS requirement), then subscribe.
        await ensurePushPermission();
        await subscribeToPush(this.publicKey);
        localStorage.setItem(SYNCED_AT_KEY, String(Date.now()));
      } catch (error) {
        if (error.message === "permission-denied") {
          this.$toast.warning(this.$t('notifications.pushPermissionDenied'), { timeout: 3000 });
        } else {
          console.error('Push subscribe failed:', error); // eslint-disable-line no-console
        }
      }
      this.show = false;
    },

    dismiss() {
      localStorage.setItem(DISMISSED_KEY, "1");
      this.show = false;
    },
  },
};
</script>

<style scoped>
.push-prompt {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 1080;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  padding: 14px 16px;
  max-width: 320px;
}

.push-prompt-text {
  font-size: 0.875rem;
  margin-bottom: 10px;
}

.push-prompt-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>
